import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Users, Plus, Pencil, Trash2, Search, Ban, CheckCircle2, Upload, Loader2, ShieldAlert, Eye, ScanLine, Heart, BarChart3, Download, TrendingUp, AlertTriangle, PackageCheck, ChevronDown, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { Header } from "@/components/Header";
import { api, resolveImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { MailCheck } from "lucide-react";

const EMPTY = {
  barcode: "", name: "", brand: "", category: "", category_type: "auto", image_url: "", ingredients_text: "",
  e_additives: "", allergens: "", sugars_g: "", salt_g: "", energy_kcal: "", protein_g: "",
  saturated_fat_g: "", fat_g: "", fiber_g: "", trans_fat: false, palm_oil: false, data_complete: false,
  certificates: { halal: false, organic: false, gost: false, eco: false },
};
const scoreCls = { green: "text-emerald-600", yellow: "text-amber-600", red: "text-red-600" };
const GRADE_COLOR = { excellent: "#16A34A", good: "#84CC16", bad: "#F97316", awful: "#DC2626" };

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [looking, setLooking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [adminForm, setAdminForm] = useState(null);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [contributions, setContributions] = useState(null);
  const [expandedAdmin, setExpandedAdmin] = useState(null);
  const [requireCode, setRequireCode] = useState(null);
  const can = (p) => !!user && (user.is_super || user.permissions?.[p]);

  const loadProducts = useCallback(() => {
    api.get(`/products/search?q=${encodeURIComponent(q)}`).then((r) => setProducts(r.data)).catch(() => {});
  }, [q]);
  const loadUsers = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  const loadAnalytics = useCallback(() => {
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);
  const loadAdmins = useCallback(() => {
    api.get("/admin/admins").then((r) => setAdmins(r.data)).catch(() => {});
  }, []);
  const loadContributions = useCallback(() => {
    api.get("/admin/contributions").then((r) => setContributions(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setRequireCode(r.data.registration_require_code)).catch(() => {});
  }, []);

  const toggleRequireCode = async (val) => {
    setRequireCode(val);
    try {
      await api.put("/admin/settings", { registration_require_code: val });
      toast.success(val ? t("admin.toast.codeOn") : t("admin.toast.codeOff"));
    } catch {
      setRequireCode(!val);
      toast.error(t("admin.toast.saveSettingFail"));
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/auth"); return; }
    loadStats();
    if (can("manage_users")) loadUsers();
    if (can("view_analytics")) loadAnalytics();
    if (can("manage_admins")) loadAdmins();
    if (user.is_super) loadContributions();
    const first = ["manage_products", "manage_users", "view_analytics", "manage_admins"]
      .find((p) => user.is_super || user.permissions?.[p]);
    const tabMap = { manage_products: "products", manage_users: "users", view_analytics: "analytics", manage_admins: "admins" };
    setTab(tabMap[first] || "products");
  }, [loading, user]);

  useEffect(() => {
    const t = setTimeout(loadProducts, 250);
    return () => clearTimeout(t);
  }, [q, loadProducts]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      barcode: p.barcode, name: p.name, brand: p.brand || "", category: p.category || "",
      category_type: p.category_type || "auto",
      image_url: p.image_url || "", ingredients_text: p.ingredients_text || "",
      e_additives: (p.e_additives || []).join(", "), allergens: (p.allergens || []).join(", "),
      sugars_g: p.sugars_g ?? "", salt_g: p.salt_g ?? "",
      energy_kcal: p.energy_kcal ?? "", protein_g: p.protein_g ?? "",
      saturated_fat_g: p.saturated_fat_g ?? "", fat_g: p.fat_g ?? "", fiber_g: p.fiber_g ?? "",
      trans_fat: !!p.trans_fat, palm_oil: !!p.palm_oil, data_complete: !!p.data_complete,
      certificates: { halal: false, organic: false, gost: false, eco: false, ...(p.certificates || {}) },
    });
    setFormOpen(true);
  };

  const lookupBarcode = async () => {
    const bc = form.barcode.trim();
    if (!bc) { toast.error(t("admin.lookup.needBarcode")); return; }
    setLooking(true);
    try {
      const { data } = await api.get(`/admin/lookup/${encodeURIComponent(bc)}`);
      if (!data.found) { toast.info(t("admin.lookup.notFound")); return; }
      const d = data.draft;
      setForm((f) => {
        const merged = { ...f };
        Object.entries(d).forEach(([k, v]) => {
          if (k === "certificates" || k === "barcode") return;
          if (v === "" || v == null || v === false) return;
          const cur = f[k];
          if (cur === "" || cur == null || cur === false || cur === "auto") merged[k] = v;
        });
        return merged;
      });
      toast.success(`${t("admin.lookup.found")}: ${data.sources.join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admin.lookup.error"));
    } finally {
      setLooking(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: res.data.url }));
      toast.success(t("admin.toast.photoOk"));
    } catch (e) {
      toast.error(t("admin.toast.photoFail"));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.barcode || !form.name) { toast.error(t("admin.toast.barcodeNameReq")); return; }
    setSaving(true);
    const payload = {
      barcode: form.barcode.trim(), name: form.name.trim(), brand: form.brand, category: form.category,
      category_type: form.category_type === "auto" ? null : form.category_type,
      image_url: form.image_url, ingredients_text: form.ingredients_text,
      e_additives: form.e_additives.split(",").map((s) => s.trim()).filter(Boolean),
      allergens: form.allergens.split(",").map((s) => s.trim()).filter(Boolean),
      certificates: form.certificates,
      sugars_g: form.sugars_g === "" ? null : parseFloat(form.sugars_g),
      salt_g: form.salt_g === "" ? null : parseFloat(form.salt_g),
      energy_kcal: form.energy_kcal === "" ? null : parseFloat(form.energy_kcal),
      protein_g: form.protein_g === "" ? null : parseFloat(form.protein_g),
      saturated_fat_g: form.saturated_fat_g === "" ? null : parseFloat(form.saturated_fat_g),
      fat_g: form.fat_g === "" ? null : parseFloat(form.fat_g),
      fiber_g: form.fiber_g === "" ? null : parseFloat(form.fiber_g),
      trans_fat: form.trans_fat, palm_oil: form.palm_oil, data_complete: form.data_complete,
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? t("admin.toast.prodUpdated") : t("admin.toast.prodAdded"));
      setFormOpen(false); loadProducts(); loadStats(); loadAnalytics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admin.toast.saveFail"));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      if (delTarget.type === "product") { await api.delete(`/admin/products/${delTarget.id}`); loadProducts(); }
      else { await api.delete(`/admin/users/${delTarget.id}`); loadUsers(); }
      toast.success(t("admin.toast.deleted"));
      loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || t("admin.toast.error")); }
    setDelTarget(null);
  };

  const toggleBlock = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}/block`);
      toast.success(u.blocked ? t("admin.toast.unblocked") : t("admin.toast.blocked"));
      loadUsers(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || t("admin.toast.error")); }
  };

  const PERM_LABELS = { manage_products: t("admin.perm.manage_products"), manage_users: t("admin.perm.manage_users"), view_analytics: t("admin.perm.view_analytics"), ai_upload: t("admin.perm.ai_upload"), manage_admins: t("admin.perm.manage_admins") };
  const PERM_KEYS = ["manage_products", "manage_users", "view_analytics", "ai_upload", "manage_admins"];

  const openCreateAdmin = () => setAdminForm({ mode: "create", name: "", email: "", password: "", permissions: {} });
  const openEditAdmin = (a) => setAdminForm({ mode: "edit", id: a.id, name: a.name, email: a.email, permissions: { ...a.permissions } });
  const togglePerm = (k) => setAdminForm((f) => ({ ...f, permissions: { ...f.permissions, [k]: !f.permissions[k] } }));

  const saveAdmin = async () => {
    if (adminForm.mode === "create" && (!adminForm.name || !adminForm.email || !adminForm.password)) {
      toast.error(t("admin.toast.adminFieldsReq")); return;
    }
    setSavingAdmin(true);
    try {
      if (adminForm.mode === "create") {
        await api.post("/admin/admins", { name: adminForm.name, email: adminForm.email, password: adminForm.password, permissions: adminForm.permissions });
        toast.success(t("admin.toast.adminCreated"));
      } else {
        await api.patch(`/admin/admins/${adminForm.id}`, { permissions: adminForm.permissions });
        toast.success(t("admin.toast.permsUpdated"));
      }
      setAdminForm(null); loadAdmins();
    } catch (e) { toast.error(e?.response?.data?.detail || t("admin.toast.error")); }
    finally { setSavingAdmin(false); }
  };

  const deleteAdmin = async (a) => {
    try { await api.delete(`/admin/admins/${a.id}`); toast.success(t("admin.toast.adminDeleted")); loadAdmins(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("admin.toast.error")); }
  };

  const exportReport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/plain;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = "nutriscan_report.txt";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("admin.toast.reportDownloaded"));
    } catch { toast.error(t("admin.toast.reportFail")); }
    finally { setExporting(false); }
  };

  const viewUser = async (u) => {
    setDetailLoading(true);
    setUserDetail({ user: u, scans: [], favorites: [], scan_count: 0, favorite_count: 0 });
    try {
      const res = await api.get(`/admin/users/${u.id}/details`);
      setUserDetail(res.data);
    } catch (e) { toast.error(t("admin.toast.profileFail")); }
    finally { setDetailLoading(false); }
  };

  if (loading || !user) return null;

  return (
    <div className="App">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold mb-1">{t("admin.title")}</h1>
        <p className="text-muted-foreground mb-6">{t("admin.subtitle")}</p>

        <div className="mb-6">
          {can("ai_upload") && (
            <Button onClick={() => navigate("/data")} data-testid="goto-data-panel" className="rounded-full bg-emerald-600 hover:bg-emerald-700">
              <BarChart3 className="w-4 h-4 mr-2" /> {t("admin.aiUpload")}
            </Button>
          )}
        </div>

        {/* stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label={t("admin.stat.products")} value={stats?.products ?? "—"} icon={Package} />
          <StatCard label={t("admin.stat.manual")} value={stats?.manual_products ?? "—"} icon={Plus} />
          <StatCard label={t("admin.stat.users")} value={stats?.users ?? "—"} icon={Users} />
          <StatCard label={t("admin.stat.blocked")} value={stats?.blocked_users ?? "—"} icon={Ban} />
        </div>

        {/* tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {can("manage_products") && (
            <Button variant={tab === "products" ? "default" : "outline"} onClick={() => setTab("products")}
              data-testid="admin-tab-products" className="rounded-full">
              <Package className="w-4 h-4 mr-2" /> {t("admin.tab.products")}
            </Button>
          )}
          {can("manage_users") && (
            <Button variant={tab === "users" ? "default" : "outline"} onClick={() => setTab("users")}
              data-testid="admin-tab-users" className="rounded-full">
              <Users className="w-4 h-4 mr-2" /> {t("admin.tab.users")}
            </Button>
          )}
          {can("view_analytics") && (
            <Button variant={tab === "analytics" ? "default" : "outline"} onClick={() => setTab("analytics")}
              data-testid="admin-tab-analytics" className="rounded-full">
              <BarChart3 className="w-4 h-4 mr-2" /> {t("admin.tab.analytics")}
            </Button>
          )}
          {can("manage_admins") && (
            <Button variant={tab === "admins" ? "default" : "outline"} onClick={() => setTab("admins")}
              data-testid="admin-tab-admins" className="rounded-full">
              <ShieldAlert className="w-4 h-4 mr-2" /> {t("admin.tab.admins")}
            </Button>
          )}
          {user?.is_super && (
            <Button variant={tab === "contributions" ? "default" : "outline"} onClick={() => setTab("contributions")}
              data-testid="admin-tab-contributions" className="rounded-full">
              <PackageCheck className="w-4 h-4 mr-2" /> {t("admin.tab.contrib")}
            </Button>
          )}
        </div>

        {tab === "contributions" && user?.is_super && (
          <div data-testid="contributions-panel" className="space-y-3">
            <div className="rounded-2xl border bg-card p-4 flex items-center gap-3" data-testid="reg-settings-card">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <MailCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{t("admin.regCode.title")}</p>
                <p className="text-xs text-muted-foreground">
                  {requireCode === false ? t("admin.regCode.off") : t("admin.regCode.on")}
                </p>
              </div>
              <Switch checked={!!requireCode} disabled={requireCode === null}
                onCheckedChange={toggleRequireCode} data-testid="reg-require-code-switch" />
            </div>

            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg">{t("admin.contrib.title")}</h3>
              <Badge variant="secondary" className="rounded-full" data-testid="contrib-total">
                {t("admin.contrib.total")}: {contributions?.total_manual ?? "—"}
              </Badge>
            </div>
            {!contributions && <p className="text-muted-foreground text-sm py-6 text-center">{t("common.loading")}</p>}
            {contributions?.admins?.map((g) => {
              const open = expandedAdmin === (g.admin_id || "__unknown__");
              return (
                <div key={g.admin_id || "__unknown__"} data-testid={`contrib-admin-${g.admin_id || "unknown"}`}
                  className="rounded-2xl border bg-card">
                  <button
                    onClick={() => setExpandedAdmin(open ? null : (g.admin_id || "__unknown__"))}
                    className="w-full flex items-center gap-3 p-4 text-left"
                    data-testid={`contrib-toggle-${g.admin_id || "unknown"}`}>
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                      {g.is_super ? <Crown className="w-5 h-5 text-amber-500" /> : <Users className="w-5 h-5 text-emerald-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{g.name}{g.is_super && <span className="ml-2 text-xs text-amber-600">{t("admin.contrib.superAdmin")}</span>}</p>
                      <p className="text-xs text-muted-foreground truncate">{g.email || "—"}</p>
                    </div>
                    <Badge className="rounded-full" data-testid={`contrib-count-${g.admin_id || "unknown"}`}>{g.count} {t("admin.contrib.items")}</Badge>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-4 pb-4">
                      {g.products.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-2">{t("admin.contrib.noProducts")}</p>
                      ) : (
                        <div className="space-y-2">
                          {g.products.map((p) => (
                            <div key={p.id} data-testid={`contrib-product-${p.id}`}
                              className="flex items-center gap-3 rounded-xl bg-muted/40 p-2 cursor-pointer hover:bg-muted"
                              onClick={() => navigate(`/product/${p.barcode}`)}>
                              <img src={resolveImage(p.image_url)} alt="" className="w-9 h-9 rounded-lg object-cover bg-white"
                                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{p.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{p.barcode}{p.category ? ` · ${p.category}` : ""}</p>
                              </div>
                              {p.created_at && <span className="text-xs text-muted-foreground shrink-0">{new Date(p.created_at).toLocaleDateString("ru-RU")}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === "products" && (
          <div className="bg-card rounded-2xl border border-border p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex flex-col sm:flex-row gap-3 justify-between mb-5">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-product-search"
                  placeholder={t("admin.prod.searchPh")} className="pl-10 rounded-xl" />
              </div>
              <Button onClick={openCreate} data-testid="add-product-button" className="rounded-full bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" /> {t("admin.prod.add")}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("admin.prod.colProduct")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("admin.prod.colBarcode")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("admin.prod.colCategory")}</TableHead>
                    <TableHead>{t("admin.prod.colScore")}</TableHead>
                    <TableHead className="text-right">{t("admin.prod.colActions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-product-row-${p.barcode}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-muted overflow-hidden flex items-center justify-center shrink-0">
                            {p.image_url && <img src={resolveImage(p.image_url)} alt="" className="w-full h-full object-contain" />}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate max-w-[180px]">{p.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{p.brand || "—"}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{p.barcode}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{p.category || "—"}</TableCell>
                      <TableCell>
                        <span className={`font-heading font-bold ${scoreCls[p.score.color]}`}>{p.score.total}</span>
                        {p.score.data?.applied && (
                          <span title={p.score.data.note} data-testid={`data-warning-${p.barcode}`} className="inline-flex items-center ml-1.5 align-middle text-amber-500">
                            <AlertTriangle className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-product-${p.barcode}`} className="rounded-full h-9 w-9">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => setDelTarget({ type: "product", id: p.id, name: p.name })} data-testid={`delete-product-${p.barcode}`} className="rounded-full h-9 w-9 text-destructive hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {products.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">{t("admin.prod.notFound")}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="bg-card rounded-2xl border border-border p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("admin.usr.colName")}</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>{t("admin.usr.colRole")}</TableHead>
                  <TableHead>{t("admin.usr.colStatus")}</TableHead>
                  <TableHead className="text-right">{t("admin.prod.colActions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`admin-user-row-${u.email}`}>
                    <TableCell className="font-medium text-sm">{u.name || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant={u.role === "admin" ? "default" : "secondary"} className="rounded-full">{u.role === "admin" ? t("admin.usr.admin") : t("admin.usr.user")}</Badge></TableCell>
                    <TableCell>
                      {u.blocked
                        ? <Badge variant="destructive" className="rounded-full">{t("admin.usr.blocked")}</Badge>
                        : <span className="inline-flex items-center gap-1 text-sm text-emerald-600"><CheckCircle2 className="w-4 h-4" /> {t("admin.usr.active")}</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => viewUser(u)} data-testid={`view-user-${u.email}`} className="rounded-full">
                          <Eye className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">{t("admin.usr.profile")}</span>
                        </Button>
                        {u.role !== "admin" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => toggleBlock(u)} data-testid={`block-user-${u.email}`} className="rounded-full">
                              {u.blocked ? t("admin.usr.unblock") : <><Ban className="w-3.5 h-3.5 mr-1" /> {t("admin.usr.block")}</>}
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => setDelTarget({ type: "user", id: u.id, name: u.email })} data-testid={`delete-user-${u.email}`} className="rounded-full h-9 w-9 text-destructive hover:text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">{t("admin.usr.none")}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {tab === "analytics" && (
          <div className="space-y-5">
            <div className="flex flex-col sm:flex-row justify-between gap-3 items-start sm:items-center">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <TrendingUp className="w-4 h-4 text-primary" /> {t("admin.an.avg")}: <span className="font-heading font-bold text-foreground text-base">{analytics?.avg_score ?? "—"}</span>
              </div>
              <Button onClick={exportReport} disabled={exporting} data-testid="export-report-button" className="rounded-full bg-primary hover:bg-primary/90">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {t("admin.an.download")}
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {/* grade distribution */}
              <div className="bg-card rounded-2xl border border-border p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <h3 className="font-heading font-bold mb-4">{t("admin.an.dist")}</h3>
                {analytics && Object.entries(GRADE_COLOR).map(([k, color]) => {
                  const val = analytics.grades[k] || 0;
                  const total = Object.values(analytics.grades).reduce((a, b) => a + b, 0) || 1;
                  return (
                    <div key={k} className="mb-3" data-testid={`analytics-grade-${k}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />{t(`grade.${k}`)}</span>
                        <span className="font-semibold">{val}</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(val / total) * 100}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* key numbers */}
              <div className="bg-card rounded-2xl border border-border p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <h3 className="font-heading font-bold mb-4">{t("admin.an.metrics")}</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Metric label={t("admin.an.mProducts")} value={analytics?.products} />
                  <Metric label={t("admin.an.mManual")} value={analytics?.manual} />
                  <Metric label={t("admin.an.mOff")} value={analytics?.off} />
                  <Metric label={t("admin.an.mUsers")} value={analytics?.users} />
                  <Metric label={t("admin.an.mScans")} value={analytics?.scans} />
                  <Metric label={t("admin.an.mFav")} value={analytics?.favorites} />
                </div>
              </div>

              {/* categories */}
              <div className="bg-card rounded-2xl border border-border p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <h3 className="font-heading font-bold mb-4">{t("admin.an.cats")}</h3>
                {analytics?.categories?.length ? analytics.categories.map((c) => (
                  <div key={c.name} className="flex justify-between py-1.5 text-sm border-b border-border last:border-0">
                    <span className="truncate">{c.name}</span><span className="font-semibold">{c.count}</span>
                  </div>
                )) : <p className="text-sm text-muted-foreground">{t("admin.an.noData")}</p>}
              </div>

              {/* top scanned */}
              <div className="bg-card rounded-2xl border border-border p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <h3 className="font-heading font-bold mb-4">{t("admin.an.top")}</h3>
                {analytics?.top_scanned?.length ? analytics.top_scanned.map((tp, i) => (
                  <div key={tp.barcode} className="flex justify-between py-1.5 text-sm border-b border-border last:border-0">
                    <span className="truncate"><span className="text-muted-foreground mr-2">{i + 1}.</span>{tp.name}</span>
                    <span className="font-semibold flex items-center gap-1"><ScanLine className="w-3.5 h-3.5 text-primary" />{tp.scans}</span>
                  </div>
                )) : <p className="text-sm text-muted-foreground">{t("admin.an.noScans")}</p>}
              </div>
            </div>
          </div>
        )}

        {tab === "admins" && (
          <div className="bg-card rounded-2xl border border-border p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex justify-between items-center mb-5">
              <p className="text-sm text-muted-foreground">{t("admin.adm.subtitle")}</p>
              <Button onClick={openCreateAdmin} data-testid="add-admin-button" className="rounded-full bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" /> {t("admin.adm.create")}
              </Button>
            </div>
            <div className="space-y-3">
              {admins.map((a) => (
                <div key={a.id} data-testid={`admin-row-${a.email}`} className="flex flex-col sm:flex-row sm:items-center gap-3 border border-border rounded-2xl p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-heading font-bold truncate">{a.name || "—"}</p>
                      {a.is_super
                        ? <Badge className="rounded-full bg-emerald-600">{t("admin.adm.chief")}</Badge>
                        : <Badge variant="secondary" className="rounded-full">{t("admin.adm.admin")}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{a.email}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {a.is_super ? <span className="text-xs text-emerald-600">{t("admin.adm.allFns")}</span>
                        : PERM_KEYS.filter((k) => a.permissions?.[k]).map((k) => (
                          <span key={k} className="text-xs bg-muted px-2 py-0.5 rounded-full">{PERM_LABELS[k]}</span>
                        ))}
                      {!a.is_super && !PERM_KEYS.some((k) => a.permissions?.[k]) && <span className="text-xs text-muted-foreground">{t("admin.adm.noPerms")}</span>}
                    </div>
                  </div>
                  {!a.is_super && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEditAdmin(a)} data-testid={`edit-admin-${a.email}`} className="rounded-full">
                        <Pencil className="w-3.5 h-3.5 mr-1" /> {t("admin.adm.rights")}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteAdmin(a)} data-testid={`delete-admin-${a.email}`} className="rounded-full h-9 w-9 text-destructive hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {admins.length === 0 && <p className="text-center py-8 text-muted-foreground">{t("admin.adm.none")}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Admin create/edit dialog */}
      <Dialog open={!!adminForm} onOpenChange={(o) => !o && setAdminForm(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="admin-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{adminForm?.mode === "create" ? t("admin.af.createTitle") : t("admin.af.editTitle")}</DialogTitle>
          </DialogHeader>
          {adminForm && (
            <div className="space-y-4 py-2">
              {adminForm.mode === "create" && (
                <>
                  <Field label={t("admin.af.name")}><Input value={adminForm.name} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} data-testid="admin-name" className="rounded-xl" /></Field>
                  <Field label={t("admin.af.email")}><Input type="email" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} data-testid="admin-email" className="rounded-xl" /></Field>
                  <Field label={t("admin.af.password")}><Input type="password" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} data-testid="admin-password" className="rounded-xl" /></Field>
                </>
              )}
              <div>
                <Label className="mb-2 block">{t("admin.af.allowed")}</Label>
                <div className="space-y-2">
                  {PERM_KEYS.map((k) => (
                    <label key={k} className="flex items-center gap-2 cursor-pointer bg-muted/40 rounded-xl px-3 py-2">
                      <Checkbox checked={!!adminForm.permissions[k]} onCheckedChange={() => togglePerm(k)} data-testid={`perm-${k}`} />
                      <span className="text-sm">{PERM_LABELS[k]}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminForm(null)} className="rounded-full">{t("common.cancel")}</Button>
            <Button onClick={saveAdmin} disabled={savingAdmin} data-testid="save-admin-button" className="rounded-full bg-primary hover:bg-primary/90">
              {savingAdmin ? t("admin.af.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product form dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? t("admin.pf.editTitle") : t("admin.pf.createTitle")}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <Field label={t("admin.pf.barcode")}>
              <div className="flex gap-2">
                <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} data-testid="form-barcode" className="rounded-xl flex-1" />
                <Button type="button" variant="outline" onClick={lookupBarcode} disabled={looking || !form.barcode.trim()}
                  data-testid="form-lookup-barcode" title={t("admin.lookup.hint")} className="rounded-xl shrink-0 gap-1.5">
                  {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {t("admin.lookup.btn")}
                </Button>
              </div>
            </Field>
            <Field label={t("admin.pf.name")}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" className="rounded-xl" /></Field>
            <Field label={t("admin.pf.brand")}><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="form-brand" className="rounded-xl" /></Field>
            <Field label={t("admin.pf.category")}><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category" className="rounded-xl" /></Field>
            <Field label={t("admin.pf.scoreType")}>
              <select value={form.category_type} onChange={(e) => setForm({ ...form, category_type: e.target.value })}
                data-testid="form-category-type"
                className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                {["auto", "general", "beverage", "cheese", "added_fats"].map((k) => <option key={k} value={k}>{t(`admin.cat.${k}`)}</option>)}
              </select>
            </Field>

            <div className="sm:col-span-2">
              <Label className="mb-1.5 block">{t("admin.pf.photo")}</Label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden flex items-center justify-center shrink-0">
                  {form.image_url && <img src={resolveImage(form.image_url)} alt="" className="w-full h-full object-contain" />}
                </div>
                <label className="flex-1">
                  <input type="file" accept="image/*" hidden data-testid="form-image-upload"
                    onChange={(e) => handleUpload(e.target.files?.[0])} />
                  <div className="flex items-center justify-center gap-2 h-11 rounded-xl border border-dashed border-border cursor-pointer hover:bg-muted transition-colors text-sm">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? t("admin.pf.uploading") : t("admin.pf.upload")}
                  </div>
                </label>
              </div>
            </div>

            <div className="sm:col-span-2">
              <Field label={t("admin.pf.ingredients")}><Textarea value={form.ingredients_text} onChange={(e) => setForm({ ...form, ingredients_text: e.target.value })} data-testid="form-ingredients" className="rounded-xl min-h-[70px]" /></Field>
            </div>
            <Field label={t("admin.pf.additives")}><Input value={form.e_additives} onChange={(e) => setForm({ ...form, e_additives: e.target.value })} data-testid="form-additives" placeholder="E330, E250" className="rounded-xl" /></Field>
            <Field label={t("admin.pf.allergens")}><Input value={form.allergens} onChange={(e) => setForm({ ...form, allergens: e.target.value })} data-testid="form-allergens" placeholder={t("admin.pf.allergensPh")} className="rounded-xl" /></Field>
            <Field label={t("admin.pf.sugar")}><Input type="number" step="0.1" value={form.sugars_g} onChange={(e) => setForm({ ...form, sugars_g: e.target.value })} data-testid="form-sugars" className="rounded-xl" /></Field>
            <Field label={t("admin.pf.salt")}><Input type="number" step="0.1" value={form.salt_g} onChange={(e) => setForm({ ...form, salt_g: e.target.value })} data-testid="form-salt" className="rounded-xl" /></Field>
            <Field label={t("admin.pf.energy")}><Input type="number" step="1" value={form.energy_kcal} onChange={(e) => setForm({ ...form, energy_kcal: e.target.value })} data-testid="form-energy" className="rounded-xl" /></Field>
            <Field label={t("admin.pf.protein")}><Input type="number" step="0.1" value={form.protein_g} onChange={(e) => setForm({ ...form, protein_g: e.target.value })} data-testid="form-protein" className="rounded-xl" /></Field>
            <Field label={t("admin.pf.satfat")}><Input type="number" step="0.1" value={form.saturated_fat_g} onChange={(e) => setForm({ ...form, saturated_fat_g: e.target.value })} data-testid="form-satfat" className="rounded-xl" /></Field>
            <Field label={t("admin.pf.fat")}><Input type="number" step="0.1" value={form.fat_g} onChange={(e) => setForm({ ...form, fat_g: e.target.value })} data-testid="form-fat" className="rounded-xl" /></Field>
            <Field label={t("admin.pf.fiber")}><Input type="number" step="0.1" value={form.fiber_g} onChange={(e) => setForm({ ...form, fiber_g: e.target.value })} data-testid="form-fiber" className="rounded-xl" /></Field>

            <div className="sm:col-span-2 flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.trans_fat} onCheckedChange={(v) => setForm({ ...form, trans_fat: !!v })} data-testid="form-transfat" /> {t("admin.pf.transfat")}
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.palm_oil} onCheckedChange={(v) => setForm({ ...form, palm_oil: !!v })} data-testid="form-palmoil" /> {t("admin.pf.palmoil")}
              </label>
            </div>

            <div className="sm:col-span-2">
              <Label className="mb-2 block">{t("admin.pf.certs")}</Label>
              <div className="flex flex-wrap gap-4">
                {["halal", "organic", "gost", "eco"].map((key) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form.certificates[key]} data-testid={`form-cert-${key}`}
                      onCheckedChange={(v) => setForm({ ...form, certificates: { ...form.certificates, [key]: !!v } })} />
                    {t(`admin.cert.${key}`)}
                  </label>
                ))}
              </div>
            </div>

            <label className="sm:col-span-2 flex items-start gap-2 cursor-pointer bg-emerald-50 border border-emerald-100 rounded-xl p-3">
              <Checkbox checked={form.data_complete} onCheckedChange={(v) => setForm({ ...form, data_complete: !!v })} data-testid="form-data-complete" className="mt-0.5" />
              <span>
                <span className="font-medium text-slate-800">{t("admin.pf.dataComplete")}</span>
                <span className="block text-xs text-slate-500">{t("admin.pf.dataCompleteHint")}</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} className="rounded-full">{t("common.cancel")}</Button>
            <Button onClick={save} disabled={saving} data-testid="save-product-button" className="rounded-full bg-primary hover:bg-primary/90">
              {saving ? t("admin.pf.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User detail dialog */}
      <Dialog open={!!userDetail} onOpenChange={(o) => !o && setUserDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="user-detail-dialog">
          {userDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">{t("admin.ud.title")}</DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-4 py-2">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="font-heading font-bold text-lg truncate">{userDetail.user.name || "—"}</p>
                  <p className="text-sm text-muted-foreground truncate">{userDetail.user.email}</p>
                </div>
                <div className="ml-auto">
                  {userDetail.user.blocked
                    ? <Badge variant="destructive" className="rounded-full">{t("admin.usr.blocked")}</Badge>
                    : <span className="inline-flex items-center gap-1 text-sm text-emerald-600"><CheckCircle2 className="w-4 h-4" /> {t("admin.usr.active")}</span>}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 py-2">
                <div className="bg-muted/50 rounded-xl p-4 text-center">
                  <p className="font-heading text-2xl font-extrabold" data-testid="detail-scan-count">{detailLoading ? "—" : userDetail.scan_count}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("admin.ud.checks")}</p>
                </div>
                <div className="bg-muted/50 rounded-xl p-4 text-center">
                  <p className="font-heading text-2xl font-extrabold" data-testid="detail-fav-count">{detailLoading ? "—" : userDetail.favorite_count}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("admin.ud.cart")}</p>
                </div>
                <div className="bg-muted/50 rounded-xl p-4 text-center">
                  <p className="font-heading text-sm font-bold">{userDetail.user.role === "admin" ? t("admin.usr.admin") : t("admin.usr.user")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("admin.ud.role")}</p>
                </div>
              </div>

              {detailLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4 py-2">
                  <ActivityList title={t("admin.ud.recent")} icon={ScanLine} items={userDetail.scans} empty={t("admin.ud.noActivity")} />
                  <ActivityList title={t("admin.ud.healthy")} icon={Heart} items={userDetail.favorites} empty={t("admin.ud.cartEmpty")} />
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-destructive" /> {t("admin.del.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.del.q")} «{delTarget?.name}»? {t("admin.del.irreversible")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-button" className="rounded-full bg-destructive hover:bg-destructive/90">{t("admin.del.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-card rounded-2xl border border-border p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <p className="font-heading text-3xl font-extrabold">{value}</p>
  </div>
);

const Field = ({ label, children }) => (
  <div><Label className="mb-1.5 block">{label}</Label>{children}</div>
);

const Metric = ({ label, value }) => (
  <div className="bg-muted/50 rounded-xl p-3">
    <p className="font-heading text-xl font-extrabold">{value ?? "—"}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

const scoreDot = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-red-500" };
const ActivityList = ({ title, icon: Icon, items, empty }) => (
  <div>
    <div className="flex items-center gap-2 mb-2 text-sm font-heading font-bold">
      <Icon className="w-4 h-4 text-primary" /> {title}
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-muted-foreground py-3">{empty}</p>
    ) : (
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {items.map((it) => (
          <div key={it.barcode} className="flex items-center justify-between gap-2 bg-muted/40 rounded-xl px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{it.name}</p>
              <p className="text-xs text-muted-foreground truncate">{it.brand || "—"}</p>
            </div>
            <span className="shrink-0 flex items-center gap-1.5 text-sm font-heading font-bold">
              <span className={`w-2 h-2 rounded-full ${scoreDot[it.score_color]}`} />{it.score_total}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);
